import type { InjectionKey, Ref } from 'vue';

/** What the tablist shows for one tab. */
export interface NxTabEntry {
  label: string;
  /** Optional counter beside the label (hidden when `undefined`). */
  count?: number;
}

/** The internal `tabsInjection` is used by all components expect the `NxTabs` because it's the dependency provider */
export const tabsInjectionKey = Symbol() as InjectionKey<{
  /** A map containing all available tabs (key: tabId, value: label + count) */
  tabs: Ref<Map<string, NxTabEntry>>;
  /** `compact` tablist: smaller tabs that scroll sideways instead of wrapping */
  compact: Ref<boolean>;
  /** `wrap` tablist: keep every tab visible across multiple rows */
  wrap: Ref<boolean>;
  /** `sticky` tablist: stays at the top of a scrolling container */
  sticky: Ref<boolean>;
  /** `spaced` tabs: the panels start a standard gap below the tab row */
  spaced: Ref<boolean>;
  /** `fill` tabs: the tablist stays, the active panel fills the height and scrolls */
  fill: Ref<boolean>;
  /** `flow` tabs: the panels do not scroll themselves, the surrounding container does */
  flow: Ref<boolean>;
  /** What the `sticky` tablist paints behind itself */
  stickyBackground: Ref<'surface' | 'page'>;
  /** The unique identifier of the currently selected tab */
  selectedTabId: Ref<string | undefined>;
  /** Selects the given tab */
  setSelectedTab: (tabId: string) => void;
  /** Appends a new tab to the end of the map */
  addTab: (tabId: string, tabLabel: string, count?: number) => () => void;
  /** Updates the label/count of a registered tab in place (keeps its position) */
  updateTab: (tabId: string, tabLabel: string, count?: number) => void;
  /** Returns the element id of the given tab */
  getTabElementId: <TabId extends string>(tabId: TabId) => `tab_${string}_${TabId}`;
  /** Returns the element id of the given tabpanel */
  getTabpanelElementId: <TabId extends string>(tabId: TabId) => `panel_${string}_${TabId}`;
}>;
